import type { LevelInfo } from "building-types/building/showroomTypes";
import type { ComponentInstance } from "building-types/components/componentInstanceTypes";
import { ComponentVerticalPlacementType } from "building-types/components/componentInstanceTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { LevelDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { SolidFloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import type { Foundation } from "building-types/foundations/foundationSystemTypes";
import type { LevelDef, LevelDefWithStackAbove } from "building-types/levels/LevelDef";
import { isDatumLevelPlacement, isStackedLevelPlacement } from "building-types/levels/levelTypes";
import type { DatumLevelPlacement, StackedLevelPlacement } from "building-types/levels/levelTypes";
import { type Level, type LevelAttributes } from "building-types/levels/levelTypes";
import type { Table } from "common/data/table";
import deepequal from "common/utils/deepequal";
import last from "common/utils/last";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { toTitleCase } from "common/utils/strings";
import {
  archetypeEntityIdFromInstanceScopedId,
  componentInstanceChainFromId,
  resolveInstanceScopedEntityId,
} from "doc/entity/componentInstanceEntityResolve";
import { STACKED_LEVEL_DATUM_ID } from "doc/utils/datumTools";
import type { LevelName, LevelNameShort } from "./levelConstants";
import { ROOF_HOSTED, BASEMENT, ITEM_INDEX, FLOOR_EXT_SHORT, FLOOR_EXT } from "./levelConstants";

export const DEFAULT_WINDOW_HEAD_HEIGHT = 96;

export const isFoundationDatumAnchor: {
  [K in LevelDatumAnchor]?: boolean;
} = {
  [LevelDatumAnchor.LEVEL_FLOOR_ELEVATION]: true,
  [LevelDatumAnchor.LEVEL_GRADE]: true,
};

export function levelNew(levelProps: Omit<Level, "handle">): Level {
  return {
    ...levelProps,
    handle: { type: "LEVEL", id: levelProps.id },
  };
}

export function levelAttributesNew(windowHeadHeight: number): LevelAttributes {
  return {
    windowHeadHeight,
  };
}

export function groupByWorldKey<T>(
  items: Table<T>,
  itemToWorldKey: (id: string, item: T) => string,
): Record<string, Table<T>> {
  const itemsByWorldKey: Record<string, Table<T>> = {};
  for (const id in items) {
    const item = items[id];
    const worldKey = itemToWorldKey(id, item);
    itemsByWorldKey[worldKey] = { ...itemsByWorldKey[worldKey], [id]: item };
  }
  return itemsByWorldKey;
}

function joinWorldKeyParts(...parts: string[]): string {
  return parts.filter((part) => part !== "").join("__");
}

/**
 * World key for component-member entities.
 * Indicates what levels are relevant for the entity.
 * Represented as the component id of the relevant archetype levels (affected by remapping instances), followed by
 * the chain of replication by instances
 */
export function worldKeyForBuildingEntity(
  memberEntityId: string,
  idToParents: Table<readonly string[]>,
  componentInstances: Table<ComponentInstance>,
): string {
  const instanceChain = componentInstanceChainFromId(memberEntityId);
  if (!instanceChain?.length) {
    return worldKeyForLevel(memberEntityId, idToParents);
  }

  const remappingIndex = instanceChain.findIndex(
    (instanceId) => componentInstances[instanceId]?.verticalPlacement.type === ComponentVerticalPlacementType.REMAPPING,
  );

  if (remappingIndex >= 0) {
    const anchorComponentId = worldKeyForLevel(instanceChain[remappingIndex], idToParents);
    return joinWorldKeyParts(anchorComponentId, ...instanceChain.slice(0, remappingIndex));
  }

  const archetypeComponentId = worldKeyForLevel(archetypeEntityIdFromInstanceScopedId(memberEntityId), idToParents);
  return joinWorldKeyParts(archetypeComponentId, ...instanceChain);
}

export function worldKeyForLevel(levelId: string, idToParents: Table<readonly string[]>): string {
  return (idToParents[levelId] ?? [])[0] ?? "";
}

export function filterLevelsByWorld(
  levels: Table<Level>,
  idToParents: Table<readonly string[]>,
  worldKey: string,
): Table<Level> {
  return objectKeyFilter(levels, (levelId) => worldKeyForLevel(levelId, idToParents) === worldKey);
}

export function fallbackLevelIdForFoundation(
  foundation: Foundation | SolidFloorAssemblyOverride,
  idToParents: Table<readonly string[]>,
  buildingLevels: Table<Level>,
): string | undefined {
  if (foundation.levelId) {
    return foundation.levelId;
  }
  const worldKey = worldKeyForLevel(foundation.parentId, idToParents);
  const filteredLevels = filterLevelsByWorld(buildingLevels, idToParents, worldKey);
  return bottomLevelId(filteredLevels) ?? undefined;
}

export function bottomLevelId(levelTable: Table<Level>): string | undefined {
  const levels = sortedLevels(levelTable);
  return levels.length > 0 ? levels[0].id : undefined;
}

export function sortedLevels(levels: Table<Level>) {
  return Object.values(levels).sort((a, b) => a.floorIndex - b.floorIndex);
}

export function sortedLevelIds(levels: Table<Level>): string[] {
  return Object.keys(levels).sort((a, b) => levels[a].floorIndex - levels[b].floorIndex);
}

export function bottomLevel(levels: Table<Level>): Level | undefined {
  const levelId = bottomLevelId(levels);
  return levelId ? levels[levelId] : undefined;
}

export function topLevelId(levelTable: Table<Level>): string | undefined {
  const levels = sortedLevels(levelTable);
  return levels.length > 0 ? last(levels).id : undefined;
}

export function topLevel(levels: Table<Level>): Level | undefined {
  const levelId = topLevelId(levels);
  return levelId ? levels[levelId] : undefined;
}

export function groundLevelIds(levelTable: Table<Level>): string[] {
  const levelIds = sortedLevelIds(levelTable);
  return levelIds.filter((id) => isGroundLevel(levelTable[id], levelTable));
}

export function groundLevels(levelTable: Table<Level>): Level[] {
  const levelIds = groundLevelIds(levelTable);
  return levelIds.map((id) => levelTable[id]);
}

export function isGroundLevel(level: Pick<Level, "floorIndex" | "levelBelow">, levelTable: Table<Level>): boolean {
  const { floorIndex, levelBelow } = level;

  // basement levels are not ground levels
  if (floorIndex < 0) {
    return false;
  }

  // levels with no level below are considered ground levels
  if (!levelBelow) {
    return true;
  }

  // levels with a level below that is below ground are considered ground levels
  const belowLevel = levelTable[levelBelow];

  return !belowLevel || belowLevel.floorIndex < 0;
}

/**
 * @deprecated
 * @see groundLevelIds
 * Whith levels placed by datums, we may have multiple ground levels.
 * This function will only return the lowest ground level id (if any).
 */
export function groundLevelId(levelTable: Table<Level>): string | undefined {
  const levelIds = groundLevelIds(levelTable);
  return levelIds.length > 0 ? levelIds[0] : undefined;
}

/**
 * @deprecated
 * @see groundLevels
 * Whith levels placed by datums, we may have multiple ground levels.
 * This function will only return the lowest ground level (if any).
 */
export function groundLevel(levels: Table<Level>): Level | undefined {
  const levelId = groundLevelId(levels);
  return levelId ? levels[levelId] : undefined;
}

/** Level visibility metadata for geometry attached to site grade. */
export interface LevelGroupAssociation {
  readonly levelId: string;
  readonly aboveLevel: boolean;
}

/**
 * Associates geometry at site grade with a level for interior-view visibility.
 * Ground-level geometry belongs to that level; without a ground level, it sits above a basement.
 */
export function getGroundAttachedLevelGroupAssociation(
  groundLevelId: string | undefined,
  bottomLevelId: string | undefined,
  bottomFloorIndex: number | undefined,
): LevelGroupAssociation | undefined {
  if (groundLevelId) {
    return { levelId: groundLevelId, aboveLevel: false };
  }
  if (bottomLevelId && bottomFloorIndex !== undefined && bottomFloorIndex < 0) {
    return { levelId: bottomLevelId, aboveLevel: true };
  }
  return undefined;
}

export function levelAboveId(levelId: string, levels: Table<Level>): string | undefined {
  return levels[levelId]?.levelAbove;
}

export function levelAbove(level: Level, levels: Table<Level>): Level | undefined {
  const aboveId = levelAboveId(level.id, levels);
  return aboveId ? levels[aboveId] : undefined;
}

export function levelBelowId(levelId: string, levels: Table<Level>): string | undefined {
  return levels[levelId]?.levelBelow;
}

export function levelBelow(level: Level, levels: Table<Level>): Level | undefined {
  const belowId = levelBelowId(level.id, levels);
  return belowId ? levels[belowId] : undefined;
}

/** Minimal level info needed for name generation functions */
export interface LevelNameInput {
  name?: string;
  floorIndex?: number;
}

export function levelName(
  level: LevelNameInput | undefined,
  short = false,
): LevelName | LevelNameShort | typeof ROOF_HOSTED | string | null {
  if (!level) {
    return ROOF_HOSTED;
  }

  if (level.floorIndex !== undefined && level.floorIndex < 0) {
    return BASEMENT;
  }
  const index = level.floorIndex !== undefined ? ITEM_INDEX[level.floorIndex] : undefined;
  if (!index) {
    // needed for plans that expect an undefined value.
    // just making it explicit
    return null;
  }

  return `${index} ${short ? FLOOR_EXT_SHORT : FLOOR_EXT}`;
}

export function levelCustomName(level: LevelNameInput): string {
  const nameFromCount = level.name?.split(" ") ?? [];
  // test if auto-generated by docEntityNameWithCount
  if (nameFromCount[0] !== "Level" || !nameFromCount[1] || isNaN(Number(nameFromCount[1]))) {
    return level.name ?? "";
  }
  return levelTitleCase(level);
}

export function levelTitleCase(level: LevelNameInput): string {
  const name: string = levelName(level) || "";
  switch (name) {
    case "ROOF HOSTED":
      return "";
    default:
      return toTitleCase(name.replace("FLR", "FLOOR"));
  }
}

export function levelIsBelow(levelA: string, levelB: string, levels: Table<Level>) {
  return levels[levelA].floorIndex < levels[levelB].floorIndex;
}

export function levelIsBelowOrEqual(levelA: string, levelB: string, levels: Table<Level>) {
  return levels[levelA].floorIndex <= levels[levelB].floorIndex;
}

export function levelIsAbove(levelA: string, levelB: string, levels: Table<Level>) {
  return levels[levelA].floorIndex > levels[levelB].floorIndex;
}

export function levelIsAboveOrEqual(levelA: string, levelB: string, levels: Table<Level>) {
  return levels[levelA].floorIndex >= levels[levelB].floorIndex;
}

export function levelIsBasement(level: Level) {
  return level.floorIndex < 0;
}

export function isDatumLevelDef(levelDef: LevelDef): levelDef is LevelDef & { placement: DatumLevelPlacement } {
  return isDatumLevelPlacement(levelDef.placement);
}

export function isStackedLevelDef(levelDef: LevelDef): levelDef is LevelDef & { placement: StackedLevelPlacement } {
  return isStackedLevelPlacement(levelDef.placement);
}

export function levelDefHasStackAbove(levelDef: LevelDef): levelDef is LevelDefWithStackAbove {
  return isStackedLevelDef(levelDef) && levelDef.placement.stackAbove !== undefined;
}

export function levelDefHasManualStackAbove(levelDef: LevelDef): levelDef is LevelDefWithStackAbove {
  return levelDefHasStackAbove(levelDef) && !levelDef.placement.automaticStacking;
}

export function getLevelDefDatumId({ placement }: LevelDef, datumAbstractions: Table<DatumAbstraction>): string {
  return isDatumLevelPlacement(placement)
    ? resolveInstanceScopedEntityId({
        path: placement.datumUsage,
        table: datumAbstractions,
        fallbackId: STACKED_LEVEL_DATUM_ID,
      })
    : STACKED_LEVEL_DATUM_ID;
}

export function getLevelDefDatumOffset({ placement }: LevelDef): number {
  return isDatumLevelPlacement(placement) ? placement.datumUsage?.offset ?? 0 : 0;
}

/**
 * Extract simplified level info from a levels table.
 * Pre-computes display names and ground level status for UI use.
 */
export function extractLevelsFromBuilding(levels: Table<Level>): LevelInfo[] {
  const sorted = sortedLevels(levels);
  // Ground level is the first level with floorIndex >= 0
  const groundLevelIndex = sorted.findIndex((level) => level.floorIndex >= 0);

  return sorted.map((level, index) => ({
    id: level.handle.id,
    name: levelCustomName(level),
    sortIndex: level.floorIndex,
    isGroundLevel: index === groundLevelIndex,
    floorElevation: level.floorElevation,
    topOfWall: level.topOfWall,
  }));
}

/**
 * Find the ground level for the stack containing `levelId`, trying three
 * strategies from most to least precise:
 *   1. Walk down `levelBelow` for a ground level in this stack.
 *   2. Match by component instance chain to find a ground level in the same instance.
 *   3. Fall back to any ground level.
 */
export function groundLevelIdForLevelStack(levelTable: Table<Level>, levelId: string): string | undefined {
  // Strategy 1: walk down the levelBelow chain looking for a ground level.
  let current = levelTable[levelId];
  while (current) {
    if (isGroundLevel(current, levelTable)) {
      return current.id;
    }
    if (!current.levelBelow) {
      break;
    }
    current = levelTable[current.levelBelow];
  }

  // Strategy 2: match on the component instance chain.
  const chain = levelTable[levelId]?.componentInstanceChain;
  const groundIds = groundLevelIds(levelTable);
  const sameStackGround = groundIds.find((id) =>
    // treat missing chains as empty so grounds in the same stack (no instance chain) match
    deepequal(chain ?? [], levelTable[id]?.componentInstanceChain ?? []),
  );

  // Strategy 3: fall back to any ground level.
  return sameStackGround ?? groundIds[0];
}
